import { getPresignedUrl, IFileUpload, uploadToStorage } from "@/api";
import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_DEC } from "@/config/constants";
import {
    alpha,
    Box,
    IconButton,
    LinearProgress,
    Stack,
    styled,
    Typography,
    useTheme,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosProgressEvent } from "axios";
import { filesize } from "filesize";
import {
    AlertCircle,
    CheckCircle2,
    FileText,
    RotateCcw,
    Trash2,
    UploadCloud,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { UploadTabSelection } from "./DocumentPicker.types";

interface ModalFileDropzoneProps {
    selection: UploadTabSelection;
    onSelectionChange: (sel: UploadTabSelection) => void;
    onUploadingChange?: (isUploading: boolean) => void;
}

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
    height: 3,
    borderRadius: 2,
    backgroundColor: theme.palette.grey[100],
    "& .MuiLinearProgress-bar": {
        borderRadius: 2,
    },
}));

export const ModalFileDropzone = ({
    selection,
    onSelectionChange,
    onUploadingChange,
}: ModalFileDropzoneProps) => {
    const theme = useTheme();

    const [localFile, setLocalFile] = useState<File | null>(
        selection?.file ?? null
    );
    const [uploadProgress, setUploadProgress] = useState<number | null>(null);
    const [localError, setLocalError] = useState<string | null>(null);

    const {
        mutate: uploadFile,
        isPending: isUploading,
        isError: uploadFailed,
        reset: resetMutation,
    } = useMutation({
        mutationKey: ["picker-file-upload"],
        mutationFn: async ({
            fileUpload,
            file,
        }: {
            fileUpload: IFileUpload;
            file: File;
        }) => {
            setUploadProgress(0);
            const presignedUrl = await getPresignedUrl(fileUpload);
            const result = await uploadToStorage(
                file,
                presignedUrl,
                (progressEvent: AxiosProgressEvent) => {
                    if (progressEvent.total) {
                        const pct = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(pct);
                    }
                }
            );
            return result;
        },
        onSuccess: (objectName) => {
            setUploadProgress(null);
            if (localFile) {
                onSelectionChange({
                    file: localFile,
                    objectName,
                    byteSize: localFile.size,
                });
            }
        },
        onError: () => {
            setUploadProgress(null);
        },
    });

    useEffect(() => {
        onUploadingChange?.(isUploading);
    }, [isUploading, onUploadingChange]);

    useEffect(() => {
        if (localFile && !selection && !isUploading && !uploadFailed) {
            uploadFile({
                fileUpload: {
                    filename: localFile.name,
                    content_type: localFile.type,
                },
                file: localFile,
            });
        }
    }, [localFile, selection, isUploading, uploadFailed, uploadFile]);

    const retryUpload = useCallback(() => {
        resetMutation();
    }, [resetMutation]);

    const onDrop = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            setLocalError(null);

            if (fileRejections.length > 0) {
                const { code } = fileRejections[0].errors[0];
                if (code === "file-too-large") {
                    setLocalError(
                        `File too large. Maximum size is ${filesize(MAX_FILE_SIZE_DEC, { round: 0 })}`
                    );
                } else if (code === "file-invalid-type") {
                    setLocalError("Please upload a PDF file");
                }
                return;
            }

            if (acceptedFiles.length > 0) {
                setLocalFile(acceptedFiles[0]);
                onSelectionChange(null);
            }
        },
        [onSelectionChange]
    );

    const removeFile = useCallback(() => {
        setLocalFile(null);
        setUploadProgress(null);
        setLocalError(null);
        resetMutation();
        onSelectionChange(null);
    }, [onSelectionChange, resetMutation]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "application/pdf": [".pdf"] },
        maxSize: MAX_FILE_SIZE_BYTES,
        maxFiles: 1,
        disabled: !!localFile,
    });

    const hasFile = !!localFile;
    const isComplete = hasFile && selection !== null && !isUploading;

    return (
        <Box
            {...(hasFile ? {} : getRootProps())}
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                px: 3,
                cursor: hasFile ? "default" : "pointer",
                transition: "background-color 0.15s ease",
                ...(isDragActive && {
                    backgroundColor: alpha(theme.palette.primary.main, 0.03),
                }),
            }}
        >
            {!hasFile && <input {...getInputProps()} />}

            {hasFile ? (
                <Box sx={{ width: "100%", maxWidth: 400 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${
                                uploadFailed
                                    ? alpha(theme.palette.error.main, 0.3)
                                    : isComplete
                                      ? alpha(theme.palette.success.main, 0.25)
                                      : theme.palette.divider
                            }`,
                            backgroundColor: theme.palette.background.paper,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 36,
                                height: 36,
                                borderRadius: 1.5,
                                flexShrink: 0,
                                backgroundColor: uploadFailed
                                    ? alpha(theme.palette.error.main, 0.08)
                                    : isComplete
                                      ? alpha(theme.palette.success.main, 0.08)
                                      : alpha(theme.palette.primary.main, 0.08),
                                color: uploadFailed
                                    ? theme.palette.error.main
                                    : isComplete
                                      ? theme.palette.success.main
                                      : theme.palette.primary.main,
                            }}
                        >
                            {uploadFailed ? (
                                <AlertCircle size={18} />
                            ) : isComplete ? (
                                <CheckCircle2 size={18} />
                            ) : (
                                <FileText size={18} />
                            )}
                        </Box>

                        <Stack flex={1} minWidth={0} gap={0.25}>
                            <Typography
                                noWrap
                                sx={{ fontSize: "0.8rem", fontWeight: 500 }}
                            >
                                {localFile.name}
                            </Typography>
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={0.75}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "0.7rem",
                                        color: "text.secondary",
                                    }}
                                >
                                    {filesize(localFile.size, { round: 1 })}
                                </Typography>
                                {isUploading && uploadProgress !== null && (
                                    <>
                                        <Typography
                                            sx={{
                                                fontSize: "0.7rem",
                                                color: "text.disabled",
                                            }}
                                        >
                                            ·
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: "0.7rem",
                                                color: "primary.main",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Uploading {uploadProgress}%
                                        </Typography>
                                    </>
                                )}
                                {uploadFailed && (
                                    <>
                                        <Typography
                                            sx={{
                                                fontSize: "0.7rem",
                                                color: "text.disabled",
                                            }}
                                        >
                                            ·
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: "0.7rem",
                                                color: "error.main",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Upload failed
                                        </Typography>
                                    </>
                                )}
                                {isComplete && (
                                    <>
                                        <Typography
                                            sx={{
                                                fontSize: "0.7rem",
                                                color: "text.disabled",
                                            }}
                                        >
                                            ·
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: "0.7rem",
                                                color: "success.main",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Ready
                                        </Typography>
                                    </>
                                )}
                            </Stack>
                            {isUploading && (
                                <Box sx={{ mt: 0.75 }}>
                                    <ProgressBar
                                        variant="determinate"
                                        value={uploadProgress ?? 0}
                                    />
                                </Box>
                            )}
                        </Stack>

                        <Stack
                            direction="row"
                            gap={0.25}
                            sx={{ flexShrink: 0 }}
                        >
                            {uploadFailed && (
                                <IconButton
                                    onClick={retryUpload}
                                    size="small"
                                    aria-label="Retry upload"
                                    sx={{
                                        color: theme.palette.primary.main,
                                        "&:hover": {
                                            backgroundColor: alpha(
                                                theme.palette.primary.main,
                                                0.08
                                            ),
                                        },
                                    }}
                                >
                                    <RotateCcw size={15} />
                                </IconButton>
                            )}
                            <IconButton
                                onClick={removeFile}
                                size="small"
                                aria-label={
                                    isUploading
                                        ? "Cancel upload"
                                        : "Remove file"
                                }
                                sx={{
                                    color: theme.palette.grey[400],
                                    "&:hover": {
                                        color: theme.palette.error.main,
                                        backgroundColor: alpha(
                                            theme.palette.error.main,
                                            0.08
                                        ),
                                    },
                                }}
                            >
                                <Trash2 size={15} />
                            </IconButton>
                        </Stack>
                    </Box>
                </Box>
            ) : (
                <Stack alignItems="center" gap={2} sx={{ userSelect: "none" }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 56,
                            height: 56,
                            borderRadius: "50%",
                            backgroundColor: isDragActive
                                ? alpha(theme.palette.primary.main, 0.1)
                                : theme.palette.grey[100],
                            color: isDragActive
                                ? theme.palette.primary.main
                                : theme.palette.grey[400],
                            transition: "all 0.2s ease",
                        }}
                    >
                        <UploadCloud
                            size={26}
                            strokeWidth={1.5}
                            style={{
                                transition: "transform 0.2s ease",
                                transform: isDragActive
                                    ? "translateY(-2px)"
                                    : "none",
                            }}
                        />
                    </Box>

                    <Stack alignItems="center" gap={0.5}>
                        <Typography
                            sx={{
                                fontSize: "0.85rem",
                                fontWeight: 500,
                                color: "text.primary",
                            }}
                        >
                            {isDragActive
                                ? "Drop your file here"
                                : "Drag and drop your PDF here"}
                        </Typography>
                    </Stack>

                    {!isDragActive && (
                        <Stack alignItems="center" gap={1.5}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={1.5}
                            >
                                <Box
                                    sx={{
                                        width: 32,
                                        height: "1px",
                                        bgcolor: "divider",
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: "0.65rem",
                                        color: "text.disabled",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.06em",
                                        fontWeight: 500,
                                    }}
                                >
                                    or
                                </Typography>
                                <Box
                                    sx={{
                                        width: 32,
                                        height: "1px",
                                        bgcolor: "divider",
                                    }}
                                />
                            </Stack>

                            <Typography
                                sx={{
                                    fontSize: "0.8rem",
                                    fontWeight: 600,
                                    color: "primary.main",
                                }}
                            >
                                Browse files
                            </Typography>
                        </Stack>
                    )}

                    <Typography
                        sx={{
                            fontSize: "0.7rem",
                            color: "text.disabled",
                            mt: 0.5,
                        }}
                    >
                        PDF files up to{" "}
                        {filesize(MAX_FILE_SIZE_DEC, { round: 0 })}
                    </Typography>

                    {localError && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 1,
                                backgroundColor: alpha(
                                    theme.palette.error.main,
                                    0.06
                                ),
                            }}
                        >
                            <AlertCircle
                                size={13}
                                color={theme.palette.error.main}
                            />
                            <Typography
                                sx={{
                                    fontSize: "0.75rem",
                                    color: "error.main",
                                    fontWeight: 500,
                                }}
                            >
                                {localError}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            )}
        </Box>
    );
};
